import { useCallback, useEffect, useState } from 'react';

import { keepOffered } from './gvScope';

const readStorage = <T>(key: string, fallback: T, revive?: (v: T) => T): T => {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return fallback;
    const parsed = JSON.parse(raw) as T;
    return revive ? revive(parsed) : parsed;
  } catch {
    return fallback;
  }
};

// useState mirrored to localStorage so a refresh keeps the value.
export const usePersistentState = <T>(
  key: string,
  initial: T,
  revive?: (stored: T) => T
): [T, React.Dispatch<React.SetStateAction<T>>] => {
  const [value, setValue] = useState<T>(() =>
    readStorage(key, initial, revive)
  );
  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Storage full or unavailable: keep working without persistence.
    }
  }, [key, value]);
  return [value, setValue];
};

// useState mirrored to a URL query param (replaceState, no history entry).
export const useQueryParamState = <T extends string>(
  param: string,
  initial: T,
  allowed: readonly T[]
): [T, (next: T) => void] => {
  const [value, setValue] = useState<T>(() => {
    const fromUrl = new URLSearchParams(window.location.search).get(param);
    return allowed.includes(fromUrl as T) ? (fromUrl as T) : initial;
  });
  const update = useCallback(
    (next: T) => {
      setValue(next);
      const url = new URL(window.location.href);
      url.searchParams.set(param, next);
      window.history.replaceState(window.history.state, '', url.toString());
    },
    [param]
  );
  return [value, update];
};

export const GV_TABS = ['marketpos', 'key', 'pos', 'price', 'raw'] as const;
export type GVTabId = (typeof GV_TABS)[number];

export const useGvTab = () =>
  useQueryParamState<GVTabId>('tab', 'marketpos', GV_TABS);

export const useGvCompetitors = () =>
  usePersistentState<string[]>('ws_gv_competitors_v1', []);

export const useGvPriceWine = () =>
  usePersistentState<string | number | null>('ws_gv_price_wine_v1', null);

export const useGvFilters = <T extends object>(defaults: T) =>
  usePersistentState<T>('ws_gv_current_filters_v1', defaults, (stored) => ({
    ...defaults,
    ...stored
  }));

interface RefSelectionState {
  clientRefs: { value: string | number }[];
  clientCustomRefs: { value: string | number }[];
}

// Persisted selections can outlive the account they were made with (another
// user on the same browser); drop references the client does not own so the
// API never receives foreign ids.
export const usePruneRefs = <T extends RefSelectionState>(
  setFilters: React.Dispatch<React.SetStateAction<T>>,
  ready: boolean,
  options: {
    client_refs?: { id: number }[];
    client_custom_refs?: { id: number }[];
  }
) => {
  const clientRefs = options.client_refs;
  const customRefs = options.client_custom_refs;
  useEffect(() => {
    if (!ready || !clientRefs || !customRefs) return;
    const owned = (refs: { id: number }[]) =>
      new Set(refs.map((ref) => String(ref.id)));
    const ownedRefs = owned(clientRefs);
    const ownedCustom = owned(customRefs);
    setFilters((previous) => {
      const refs = keepOffered(previous.clientRefs, ownedRefs, (r) =>
        String(r.value)
      );
      const custom = keepOffered(previous.clientCustomRefs, ownedCustom, (r) =>
        String(r.value)
      );
      return refs === previous.clientRefs &&
        custom === previous.clientCustomRefs
        ? previous
        : { ...previous, clientRefs: refs, clientCustomRefs: custom };
    });
  }, [setFilters, ready, clientRefs, customRefs]);
};
